/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {MRT_ColumnDef} from 'material-react-table';
import React, {useEffect, useMemo, useState} from 'react';
import VAR_COMMON_CONSTANTS from '../../../../../constants/globalVariables';
import {
  selectCoverageRequirementsDataState,
  selectDatasetsExecutionStatus,
  selectDatasetsLocalState,
  selectRedlineDatasetIdsToData,
  selectStudyIdToTreeTypeToTree
} from '../../../../../redux/ducks/CoverageRequirements/slice';
import {useAppSelector} from '../../../../../redux/hooks';
import ArgosFlexbox from '../../../../components/Containers/ArgosFlexbox';
import {coverageRequirementsDetails} from '../../constants';
import CovAppReqTable from '../../Table/CovAppReqTable';
import {processRedlineData} from '../../utils';
import {
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM,
  EDatasetExecutionStatus,
  MERGED_DATASET_TREE_TYPE,
  SELECTED_DATASET_ID_URL_PARAM,
  SELECTED_DATASET_NAME_URL_PARAM,
  SELECTED_DATASET_TYPE_URL_PARAM,
  SINGLE_DATASET_TREE_TYPE
} from '../constants';
import {getObjectDisplayName, renderLink} from './CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties';
import {DashboardProps, DashboardRow} from './ExecutionStatusTab/constants';
import Link from "@mui/material/Link";

interface Props extends DashboardProps {
}


/**
 * DashboardTab displays a table of completed execution redline datasets of a particular study
 * inside Coverage App Requirements. The table allows for filtering datasets based on the
 * Driving Scenario Properties.
 */
const DashboardTab: React.FC<Props> = (props) => {
  const [isFetching, setIsFetching] = useState(false)
  const coverageRequirementsData = useAppSelector(selectCoverageRequirementsDataState)
  const [dashboardTableRows, setDashboardTableRows] = useState<DashboardRow[]>([])
  const redlineDatasetIdsToData = useAppSelector(selectRedlineDatasetIdsToData)
  const rawRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, SINGLE_DATASET_TREE_TYPE, props.studyId))
  const mergedRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, MERGED_DATASET_TREE_TYPE, props.studyId))
  const datasetExecutionStatusMap: {
    [id: string]: EDatasetExecutionStatus
  } = useAppSelector((state) => selectDatasetsExecutionStatus(state))
  const datasetsLocalState = useAppSelector(selectDatasetsLocalState)


  // Fetch datasets
  useEffect(() => {
    setIsFetching(true)
    const _rows = processRedlineData(
      props.studyId ?? null,
      redlineDatasetIdsToData,
      rawRedlineTreeDbItem,
      mergedRedlineTreeDbItem,
      datasetExecutionStatusMap,
      datasetsLocalState,
      dashboardTableRows,
      EDatasetExecutionStatus.COMPLETED
    )
    setIsFetching(false)
    if (_rows == null) return
    setDashboardTableRows(_rows)
  }, [props.studyId,
    redlineDatasetIdsToData,
    coverageRequirementsData,
    rawRedlineTreeDbItem,
    mergedRedlineTreeDbItem]);

  const renderObjCell = (obj: any) => {
    const objName = obj?.name ?? null
    const objCategory = obj?.category ?? null
    const variantName = obj?.variantName ?? null
    const cellValue = getObjectDisplayName(objCategory, objName, variantName)
    return (
      <Link
        onClick={() => window.open(`/objectDetails?id=${obj?.objectId}`, '_blank')}
        underline="hover"
        target="_blank"
        rel="noreferrer"
        sx={{cursor: 'pointer'}}
      >
        {cellValue}
      </Link>
    )
  }

  const formatRows = (): MRT_ColumnDef<any, unknown>[] | any[] => {
    const rows: any[] = []
    rows.push({accessorKey: "type", header: "Type", size: 100})
    rows.push({
      accessorKey: "path",
      header: "Completed " + VAR_COMMON_CONSTANTS.VAR_DATA_SET,
      Cell: ({row}: { row: any }) => {
        const name = row?.original?.name
        const path = row?.original?.path
        const id = row?.original?.id
        const type = row?.original?.type
        const _tabName = type === "Single" ? "Raw " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS : "Merged " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS
        const studyId = props.studyId ? props.studyId : row?.original?.studyId
        return (
          <ArgosFlexbox>
            {
              renderLink(
                path,
                `${coverageRequirementsDetails}`
                + `?${SELECTED_DATASET_ID_URL_PARAM}=${id}`
                + `&${SELECTED_DATASET_NAME_URL_PARAM}=${name}`
                + `&${SELECTED_DATASET_TYPE_URL_PARAM}=${type}`
                + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM}=Coverage Analysis`
                + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM}=${_tabName}`
                + `&${COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM}=${studyId}`
              )
            }
          </ArgosFlexbox>
        )
      },
      sortingFn: (rowA: any, rowB: any, columnId: any): number => props.sortFu(rowA.getValue(columnId), rowB.getValue(columnId)),
      size: 200
    })
    if (redlineDatasetIdsToData && Object.values(redlineDatasetIdsToData)[0]) {
      const properties = Object.values(redlineDatasetIdsToData)[0].driving_scenario_properties
      if (!properties) {
        return rows
      }
      properties.forEach((property: { type: any; }) => {
        var propertyKey = property.type.toLowerCase();
        propertyKey = propertyKey.replace(/\([\s\S]*?\)/g, '').replaceAll(' ', ''); // property names with parantheses
        if (rows.find((column) => column.header === property.type)) return
        let size = 200
        if (property.type === 'Object') {
          size = 600
        }
        rows.push(
          {
            header: property.type,
            accessorFn: (row: { [x: string]: { name: string | undefined; property_link: string | undefined }[]; }) => {
              return (row[propertyKey]?.map((property: { name: string | undefined; property_link: string | undefined; }, index: number, array: string | any[]) => (
                index + 1 === array.length ? property.name : property.name + " , "
              )) ?? '')
            },
            Cell: ({row}: { row: any }) => {
              if (property.type === 'Object') {
                return (
                  <div style={{display: 'flex', flexFlow: 'column'}}>
                    {
                      row.original[propertyKey]?.map((property: any, index: number, array: string | any[]) => (
                        <div>
                          {renderObjCell(property)}
                        </div>
                      ))
                    }
                  </div>
                )
              }
              return (<ArgosFlexbox style={{gap: "10px"}}>
                {
                  row.original[propertyKey]?.map((property: { name: string | undefined; property_link: string | undefined; }, index: number, array: string | any[]) => (
                    <ArgosFlexbox>
                      {renderLink(property.name, property.property_link)}
                      {index + 1 === array.length ? "" : ","}
                    </ArgosFlexbox>
                  ))
                }
              </ArgosFlexbox>)
            },
            sortingFn: (rowA: any, rowB: any, columnId: any): number => props.sortFu(rowA.getValue(columnId), rowB.getValue(columnId)),
            size: size,
            // muiTableBodyCellProps: {
            //   sx: {
            //     whiteSpace: 'normal', // Allow text to wrap
            //     wordBreak: 'break-word', // Break words if necessary
            //   },
            // },
          },
        )
      });
    }
    return rows
  }

  // Each dataset has a Type and Name
  var defaultColumns = useMemo<MRT_ColumnDef<DashboardRow | any>[]>(
    formatRows,
    [coverageRequirementsData, redlineDatasetIdsToData]
  )

  return <CovAppReqTable
    key={"dashboardtabTable"}
    isLoadingInitialData={false}
    columns={defaultColumns}
    data={dashboardTableRows}
    isLandingPage={false}
    isFetching={isFetching}
    studyId={props.studyId ?? ""}
    studyIds={props.studyIds ?? []}
    onlyShowInProgressDatasets={false}
  />
};

export default DashboardTab;